import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useApiResource } from '../../lib/useApiResource';
import { StatusBadge } from '../../components/StatusBadge';
import { listPreviews, listTemplates } from './api';
import { EmailTemplateEditor } from './EmailTemplateEditor';
import type { DeliveryRule, EmailAudience, EmailLanguage, EmailTemplateSummary } from './types';

const AUDIENCES: { id: 'all' | EmailAudience; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'patient', label: 'Patients' },
  { id: 'specialist', label: 'Specialists' },
  { id: 'care-circle', label: 'Care circle' },
];

const DELIVERY_LABEL: Record<DeliveryRule, string> = {
  always: 'Always sent',
  appointmentAlerts: 'Appointment alerts',
  emailNotifications: 'Email notifications',
  weeklyReports: 'Weekly reports',
};

/**
 * Email Templates — every email the platform sends, as the recipient will see it.
 *
 * The gallery shows the real render of each template with sample data, in the language you pick.
 * Opening one edits its structure (subject, hero, blocks) with a live preview; publishing is
 * immediate and per language, every publish is a version, and Reset returns to the text shipped
 * with the platform. The design itself (brand bar, colours, spacing) is not editable, so no edit
 * can break the layout or the dark-mode and mobile rendering.
 */
export function EmailTemplatesPage() {
  const [language, setLanguage] = useState<EmailLanguage>('en');
  const [audience, setAudience] = useState<'all' | EmailAudience>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const templates = useApiResource<EmailTemplateSummary[]>(listTemplates, []);
  const previews = useApiResource(() => listPreviews(language), [language]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (templates.data ?? []).filter(
      (t) =>
        (audience === 'all' || t.audience === audience) &&
        (!needle || `${t.name} ${t.trigger} ${t.key}`.toLowerCase().includes(needle)),
    );
  }, [templates.data, audience, query]);

  if (selected) {
    return (
      <EmailTemplateEditor
        templateKey={selected}
        onBack={() => setSelected(null)}
        onChanged={() => {
          templates.reload();
          previews.reload();
        }}
      />
    );
  }

  if (templates.loading && !templates.data) {
    return <div className="empty-state" data-testid="email-templates-loading">Loading emails…</div>;
  }
  if (templates.error) {
    return (
      <div data-testid="email-templates-error">
        <div className="error-banner">{templates.error}</div>
        <button type="button" className="btn btn-secondary" onClick={templates.reload}>Retry</button>
      </div>
    );
  }

  const all = templates.data ?? [];
  const customised = all.filter((t) => t.overridden.length > 0).length;

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Email Templates</h1>
          <p>
            Every email the platform sends, in the MedicLynkOS design. Open one to change its words and structure;
            publishing is immediate and per language, and you can always return to the shipped text.
          </p>
        </div>
        <div className="dashboard-links">
          <StatusBadge label={`${all.length} emails`} />
          <StatusBadge label={`${customised} customised`} tone={customised ? 'good' : 'neutral'} />
        </div>
      </div>

      <div className="et-toolbar">
        <div className="ps-seg" role="group" aria-label="Audience">
          {AUDIENCES.map((a) => (
            <button key={a.id} type="button" className={audience === a.id ? 'active' : undefined} aria-pressed={audience === a.id} onClick={() => setAudience(a.id)} data-testid={`email-audience-${a.id}`}>
              {a.label}
            </button>
          ))}
        </div>
        <div className="ps-rail-search et-search">
          <Search size={13} aria-hidden />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find an email…" aria-label="Find an email" data-testid="email-filter" />
        </div>
        <div className="ps-seg" role="group" aria-label="Preview language">
          {(['en', 'es'] as const).map((code) => (
            <button key={code} type="button" className={language === code ? 'active' : undefined} aria-pressed={language === code} onClick={() => setLanguage(code)} data-testid={`email-gallery-lang-${code}`}>
              {code.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="ps-rail-empty">No email matches that filter.</p>
      ) : (
        <div className="et-grid" data-testid="email-grid">
          {filtered.map((t) => {
            const html = previews.data?.[t.key]?.html;
            return (
              <button key={t.key} type="button" className="et-card" onClick={() => setSelected(t.key)} data-testid={`email-card-${t.key}`}>
                <span className="et-thumb" aria-hidden>
                  {html ? (
                    <iframe title={`${t.name} preview`} sandbox="" srcDoc={html} tabIndex={-1} className="et-thumb-frame" />
                  ) : (
                    <span className="et-thumb-empty">{previews.loading ? 'Rendering…' : 'No preview'}</span>
                  )}
                </span>
                <span className="et-card-body">
                  <span className="et-card-name">{t.name}</span>
                  <span className="et-card-trigger">{t.trigger}</span>
                  <span className="et-card-tags">
                    <span className="ps-chip">{DELIVERY_LABEL[t.delivery]}</span>
                    {(['en', 'es'] as const).map((code) => (
                      <span key={code} className={`ps-chip${t.overridden.includes(code) ? ' token' : ''}`} title={t.overridden.includes(code) ? 'Customised' : 'Shipped text'}>
                        {code.toUpperCase()}{t.overridden.includes(code) ? ' ●' : ''}
                      </span>
                    ))}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
